import React, { useState, useEffect } from 'react';
import { servicesAPI, locationsAPI } from '../../../services/api';
import ImageUploader from '../Shared/ImageUploader';
import '../Shared/FormStyles.scss';
import './ServicesManager.scss';

const ServicesManager = () => {
  const [services, setServices] = useState([]);
  const [allLocations, setAllLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    image: '',
    description: '',
    content: '',
    locations: [],
    isActive: true,
  });
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    fetchServices();
    fetchLocations();
  }, []);

  const fetchLocations = async () => {
    try {
      const response = await locationsAPI.getAll();
      setAllLocations(response.data);
    } catch (err) {
      console.error('Failed to fetch locations', err);
    }
  };

  const fetchServices = async () => {
    try {
      const response = await servicesAPI.getAllAdmin();
      setServices(response.data);
    } catch (err) {
      setError('Failed to fetch services');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    try {
      if (editing) {
        await servicesAPI.update(editing._id, formData);
        setSuccess('Service updated successfully');
      } else {
        await servicesAPI.create(formData);
        setSuccess('Service created successfully');
      }
      resetForm();
      await fetchServices();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to save service');
    }
  };

  const handleEdit = (service) => {
    setEditing(service);
    setFormData({
      title: service.title,
      image: service.image,
      description: service.description,
      content: service.content || '',
      locations: (service.locations || []).map((loc) => (typeof loc === 'object' ? loc._id : loc)),
      isActive: service.isActive,
    });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this service?')) return;

    try {
      await servicesAPI.delete(id);
      setSuccess('Service deleted successfully');
      fetchServices();
    } catch (err) {
      setError('Failed to delete service');
    }
  };

  const handleLocationToggle = (locationId) => {
    setFormData((prev) => ({
      ...prev,
      locations: prev.locations.includes(locationId)
        ? prev.locations.filter((id) => id !== locationId)
        : [...prev.locations, locationId],
    }));
  };

  const resetForm = () => {
    setEditing(null);
    setFormData({
      title: '',
      image: '',
      description: '',
      content: '',
      locations: [],
      isActive: true,
    });
  };

  if (loading) return <div>Loading...</div>;

  return (
    <div className="services-manager">
      <div className="admin-form">
        <h2>{editing ? 'Edit Service' : 'Add New Service'}</h2>
        <div style={{ marginBottom: '20px', padding: '15px', backgroundColor: '#e3f2fd', borderRadius: '5px' }}>
          <strong>Note:</strong> Each service here gets its own page on the website at <code>/services/&lt;id&gt;</code>, linked from the card on the Services page.
        </div>
        {error && <div className="error-message">{error}</div>}
        {success && <div className="success-message">{success}</div>}
        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label>Title *</label>
            <input
              type="text"
              value={formData.title}
              onChange={(e) => { const val = e.target.value; setFormData((prev) => ({ ...prev, title: val })); }}
              required
            />
          </div>
          <ImageUploader
            value={formData.image}
            onChange={(url) => setFormData((prev) => ({ ...prev, image: url }))}
            label="Service Image"
          />
          <div className="form-group">
            <label>Description (max 150 characters)</label>
            <textarea
              value={formData.description}
              onChange={(e) => { const val = e.target.value; if (val.length <= 150) setFormData((prev) => ({ ...prev, description: val })); }}
              rows={3}
              maxLength={150}
            />
            <small style={{ color: formData.description.length >= 140 ? '#e74c3c' : '#888' }}>
              {formData.description.length}/150
            </small>
          </div>
          <div className="form-group">
            <label>Full Content (shown on the service's own page)</label>
            <textarea
              value={formData.content}
              onChange={(e) => { const val = e.target.value; setFormData((prev) => ({ ...prev, content: val })); }}
              rows={8}
              placeholder="Leave blank to show default placeholder content for this service."
            />
          </div>
          {allLocations.length > 0 && (
            <div className="form-group">
              <label>Locations</label>
              <div className="locations-checklist">
                {allLocations.map((loc) => (
                  <label key={loc._id} className="checklist-item">
                    <input
                      type="checkbox"
                      checked={formData.locations.includes(loc._id)}
                      onChange={() => handleLocationToggle(loc._id)}
                    />
                    {loc.name}
                  </label>
                ))}
              </div>
            </div>
          )}
          <div className="form-group">
            <label>
              <input
                type="checkbox"
                checked={formData.isActive}
                onChange={(e) => { const val = e.target.checked; setFormData((prev) => ({ ...prev, isActive: val })); }}
              />
              Active
            </label>
          </div>
          <div className="form-actions">
            <button type="submit" className="dashboard-btn btn-primary">
              {editing ? 'Update' : 'Create'}
            </button>
            {editing && (
              <button type="button" className="dashboard-btn btn-secondary" onClick={resetForm}>
                Cancel
              </button>
            )}
          </div>
        </form>
      </div>

      <div className="data-table" style={{ marginTop: '30px' }}>
        <h2 style={{ padding: '20px', margin: 0 }}>All Services</h2>
        {services.length === 0 ? (
          <div className="empty-state">No services found</div>
        ) : (
          <table>
            <thead>
              <tr>
                <th>Title</th>
                <th>Image</th>
                <th>Locations</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {services.map((service) => (
                <tr key={service._id}>
                  <td>{service.title}</td>
                  <td>
                    {service.image && (
                      <img src={service.image} alt={service.title} style={{ width: '50px', height: '50px', objectFit: 'cover' }} />
                    )}
                  </td>
                  <td>
                    {(service.locations || [])
                      .map((loc) => (typeof loc === 'object' ? loc.name : loc))
                      .join(', ') || '—'}
                  </td>
                  <td>{service.isActive ? 'Active' : 'Inactive'}</td>
                  <td>
                    <div className="table-actions">
                      <button className="btn-edit" onClick={() => handleEdit(service)}>
                        Edit
                      </button>
                      <button className="btn-delete" onClick={() => handleDelete(service._id)}>
                        Delete
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

export default ServicesManager;
